import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Card, Failed, Loading, Tile } from '@/components/bits'
import { useOrg } from '@/org/OrgProvider'
import { useAsync } from '@/data/useAsync'
import { loadEpisode, recordAcknowledgement, saveInterview } from '@/data/sickness'
import type { RtwAckMethod, RtwAnswer, RtwDraft } from '@/data/sickness'
import { shortDate } from '@/lib/format'

/**
 * The return to work interview (HHR176-1), as a screen.
 *
 * The paper form asked a manager to write down eleven things, six of which the
 * system already knew: the name, job title, department, both dates and both day
 * counts. Those are shown at the top, computed, and cannot be typed. A form
 * where somebody re-types a date is a form where the two dates eventually
 * disagree and nobody knows which is right.
 *
 * "Number of days absent in the last 6 months" is the one that pays for doing
 * this at all. On paper it means going through a personnel file, so it is the
 * field managers most often leave blank or get wrong, and it is the only field
 * on the form that would tell you there is a pattern worth talking about.
 *
 * Every yes/no defaults to "Not recorded" rather than "No", because a tick box
 * cannot tell the two apart and an audit needs to.
 */

const ANSWERS: { value: RtwAnswer; label: string }[] = [
  { value: 'unknown', label: 'Not recorded' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'not_applicable', label: 'Not applicable' },
]

const ACK: { value: RtwAckMethod; label: string }[] = [
  { value: 'emailed_copy', label: 'Emailed a copy' },
  { value: 'printed_copy_given', label: 'Printed and handed over' },
  { value: 'in_app', label: 'They confirmed in the app' },
  { value: 'declined', label: 'Offered, they declined' },
]

function Answer({
  label,
  value,
  onChange,
  hint,
}: {
  label: string
  value: RtwAnswer
  onChange: (v: RtwAnswer) => void
  hint?: string
}) {
  return (
    <label className="stackfield">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value as RtwAnswer)}>
        {ANSWERS.map((a) => (
          <option key={a.value} value={a.value}>
            {a.label}
          </option>
        ))}
      </select>
      {hint !== undefined && <span className="note">{hint}</span>}
    </label>
  )
}

export function ReturnToWork() {
  const { episodeId } = useParams<{ episodeId: string }>()
  const { current } = useOrg()
  const loaded = useAsync((_s) => loadEpisode(episodeId ?? ''), [episodeId])

  const [draft, setDraft] = useState<RtwDraft | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  if (loaded.state === 'loading') return <Loading what="the absence" />
  if (loaded.state === 'error') return <Failed message={loaded.message} onRetry={loaded.reload} />

  const { episode: e, interview } = loaded.data

  const d: RtwDraft =
    draft ?? {
      conductedByName: interview?.conductedByName ?? '',
      interviewAt: interview?.interviewAt?.slice(0, 16) ?? new Date().toISOString().slice(0, 16),
      reasonForAbsence: interview?.reasonForAbsence ?? '',
      visitedDoctorOrHospital: interview?.visitedDoctorOrHospital ?? 'unknown',
      visitDetail: interview?.visitDetail ?? '',
      prescribedMedication: interview?.prescribedMedication ?? 'unknown',
      feelingNow: interview?.feelingNow ?? '',
      followedReportingProcedure: interview?.followedReportingProcedure ?? 'unknown',
      sicknessDocsReviewed: interview?.sicknessDocsReviewed ?? 'unknown',
      otherComments: interview?.otherComments ?? '',
    }

  const set = <K extends keyof RtwDraft>(key: K, value: RtwDraft[K]) =>
    setDraft({ ...d, [key]: value })

  async function save(complete: boolean) {
    setBusy(true)
    setError(null)
    setSaved(null)
    try {
      await saveInterview(e.episodeId, current.id, d, complete)
      setSaved(complete ? 'Interview completed and recorded.' : 'Saved as a draft.')
      loaded.reload()
      setDraft(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const done = interview?.completedAt ?? null

  return (
    <section className="screen">
      <p className="note">
        <Link to="/absence">Back to sickness</Link>
      </p>
      <h1>Return to work interview</h1>
      <p className="lede">
        <strong>{e.teamMember}</strong>
        {e.jobTitle !== null && <> · {e.jobTitle}</>}
        {e.department !== null && <> · {e.department}</>}
      </p>

      {/* Everything the paper form made somebody look up. Not editable. */}
      <div className="tiles">
        <Tile
          label="Absence"
          value={
            <>
              {shortDate(e.firstAbsentDate)}
              {e.lastAbsentDate !== null && <> to {shortDate(e.lastAbsentDate)}</>}
            </>
          }
        />
        <Tile label="Working days absent" value={e.workingDaysAbsent}>
          {e.daysNotYetClassified > 0 && (
            <div className="note">{e.daysNotYetClassified} not yet matched to the rota</div>
          )}
        </Tile>
        <Tile label="Days absent, last 6 months" value={e.daysAbsentLast6Months}>
          <div className="note">across {e.absencesLast6Months} separate absence(s)</div>
        </Tile>
      </div>

      {done !== null && (
        <p className="note">
          Completed {shortDate(done.slice(0, 10))}. Editing it and saving again records the change
          in the audit log rather than replacing what was there silently.
        </p>
      )}

      <Card title="Interview">
        <label className="stackfield">
          <span>Manager conducting the interview</span>
          <input
            type="text"
            value={d.conductedByName}
            placeholder="Their name"
            onChange={(ev) => set('conductedByName', ev.target.value)}
          />
        </label>
        <label className="stackfield">
          <span>Date and time of interview</span>
          <input
            type="datetime-local"
            value={d.interviewAt}
            onChange={(ev) => set('interviewAt', ev.target.value)}
          />
        </label>
      </Card>

      <Card title="The conversation">
        <p className="note">
          Record what you need for the absence and nothing more. This is health information about a
          named person, so a category is usually enough and a diagnosis is rarely necessary.
        </p>

        <label className="stackfield">
          <span>Reason for absence</span>
          <input
            type="text"
            value={d.reasonForAbsence}
            placeholder="e.g. stomach upset, back pain"
            onChange={(ev) => set('reasonForAbsence', ev.target.value)}
          />
        </label>

        <Answer
          label="Did they visit their doctor or hospital?"
          value={d.visitedDoctorOrHospital}
          onChange={(v) => set('visitedDoctorOrHospital', v)}
        />

        {d.visitedDoctorOrHospital === 'yes' && (
          <label className="stackfield">
            <span>Anything from that which affects their work</span>
            <input
              type="text"
              value={d.visitDetail}
              placeholder="e.g. signed off from lifting for two weeks"
              onChange={(ev) => set('visitDetail', ev.target.value)}
            />
          </label>
        )}

        <Answer
          label="Were they prescribed any medication?"
          value={d.prescribedMedication}
          onChange={(v) => set('prescribedMedication', v)}
          hint="Asked only because medication can affect fitness for particular duties. Do not record what it was. Not recorded is a perfectly good answer."
        />

        <label className="stackfield">
          <span>How are they feeling now?</span>
          <input
            type="text"
            value={d.feelingNow}
            onChange={(ev) => set('feelingNow', ev.target.value)}
          />
        </label>

        <Answer
          label="Did they follow the reporting procedure?"
          value={d.followedReportingProcedure}
          onChange={(v) => set('followedReportingProcedure', v)}
        />

        <Answer
          label="Sickness documents reviewed and filed?"
          value={d.sicknessDocsReviewed}
          onChange={(v) => set('sicknessDocsReviewed', v)}
        />

        <label className="stackfield">
          <span>Any other comments</span>
          <input
            type="text"
            value={d.otherComments}
            onChange={(ev) => set('otherComments', ev.target.value)}
          />
        </label>

        <div className="actions">
          <button
            className="btn primary"
            type="button"
            disabled={busy}
            onClick={() => void save(true)}
          >
            {busy ? 'Saving…' : done === null ? 'Complete the interview' : 'Save changes'}
          </button>
          <button className="btn quiet" type="button" disabled={busy} onClick={() => void save(false)}>
            Save as a draft
          </button>
        </div>
        <p className="note">
          A draft is a real state. These conversations get interrupted, and losing what was said
          because somebody could not finish in one sitting is how people go back to paper.
        </p>

        {error !== null && <p className="note sev-critical">{error}</p>}
        {saved !== null && <p className="note">{saved}</p>}
      </Card>

      <Card title="Their copy">
        <p className="note">
          The procedure requires the team member to get a copy. There are no signatures: who
          completed this and when is recorded against the account that did it, and every later
          change is in the audit log, which proves more than a signature on a page in a filing
          cabinet.
        </p>
        {interview?.ackAt != null ? (
          <p>
            Recorded {shortDate(interview.ackAt.slice(0, 10))} ·{' '}
            {ACK.find((a) => a.value === interview.ackMethod)?.label ?? interview.ackMethod}
          </p>
        ) : (
          <div className="actions">
            {ACK.map((a) => (
              <button
                key={a.value}
                className="btn quiet sm"
                type="button"
                disabled={busy || interview === null}
                onClick={() => {
                  setBusy(true)
                  recordAcknowledgement(e.episodeId, a.value)
                    .then(() => loaded.reload())
                    .catch((c: unknown) =>
                      setError(c instanceof Error ? c.message : String(c)),
                    )
                    .finally(() => setBusy(false))
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
        {interview === null && (
          <p className="note">Save the interview first, then record how they got their copy.</p>
        )}
      </Card>
    </section>
  )
}
